import { Extension } from '@tiptap/core'
import type { Editor, Range } from '@tiptap/core'
import type { MutableRefObject } from 'react'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'

/** Something the editor linked to after creating it. */
export type CreatedEntity = { id: string; label: string }

/**
 * App actions an editor surface can offer (design 7a/7b): the page decides
 * what "create a task" means for it (which project, which defaults), the
 * editor only asks. Absent actions simply hide their commands.
 */
export type EditorActions = {
  createTask?: (name: string) => Promise<CreatedEntity | null>
  createSubPage?: (title: string) => Promise<CreatedEntity | null>
}

type MolyActionsStorage = { getActions: () => EditorActions }

/**
 * Exposes the host's actions to commands (slash menu, selection toolbar)
 * through editor storage. Read through a ref so the editor, which is created
 * once, always sees the latest callbacks.
 */
export function createMolyActionsExtension(actionsRef: MutableRefObject<EditorActions>) {
  return Extension.create<Record<string, never>, MolyActionsStorage>({
    name: 'molyActions',
    addStorage() {
      return { getActions: () => actionsRef.current }
    },
  })
}

export function getEditorActions(editor: Editor): EditorActions {
  const storage = (editor.storage as unknown as Partial<Record<'molyActions', MolyActionsStorage>>).molyActions
  return storage?.getActions() ?? {}
}

/**
 * Insert a link chip to a task or doc. Uses the existing `entityLink` node, so
 * every client (web, mobile, MCP) already understands it.
 */
export function insertEntityLink(
  editor: Editor,
  entity: CreatedEntity,
  entityType: typeof ENTITY_TYPES.WORK_ITEM | typeof ENTITY_TYPES.DOCUMENT,
  range?: Range,
) {
  const chain = editor.chain().focus()
  if (range) chain.deleteRange(range)
  chain
    .insertContent([
      { type: 'entityLink', attrs: { id: entity.id, label: entity.label, entityType, icon: null } },
      { type: 'text', text: ' ' },
    ])
    .run()
}

/**
 * Turn the text of the current line into a task: the line's text becomes the
 * task name and is replaced with a live link to the new task.
 */
export async function createTaskFromCurrentLine(editor: Editor, fallbackName: string) {
  const { createTask } = getEditorActions(editor)
  if (!createTask) return
  const { $from } = editor.state.selection
  const blockStart = $from.start()
  const blockEnd = $from.end()
  const name = editor.state.doc.textBetween(blockStart, blockEnd, ' ').trim() || fallbackName
  const created = await createTask(name)
  if (!created) return
  insertEntityLink(editor, created, ENTITY_TYPES.WORK_ITEM, { from: blockStart, to: blockEnd })
}

/** Turn the selected text into a task and replace it with a link (design 7b). */
export async function createTaskFromSelection(editor: Editor) {
  const { createTask } = getEditorActions(editor)
  if (!createTask) return
  const { from, to } = editor.state.selection
  const name = editor.state.doc.textBetween(from, to, ' ').trim()
  if (!name) return
  const created = await createTask(name)
  if (!created) return
  insertEntityLink(editor, created, ENTITY_TYPES.WORK_ITEM, { from, to })
}
